import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, output, computed, effect, input, signal, viewChild, inject, Signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { startWith, Subject, Subscription } from 'rxjs';
import { FilterChange, FilterConfig, FilterOption, MY_DATE_FORMATS } from '@app/shared/types/ui';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

@Component({
  selector: 'app-table-filters',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatAutocompleteModule
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'es-MX' },
    { provide: DateAdapter, useClass: NativeDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
  templateUrl: './table-filters.component.html',
  styleUrls: ['../data-table/data-table.component.scss', './table-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableFiltersComponent implements OnDestroy {
  private snackBar = inject(MatSnackBar);
  filters = input<FilterConfig[]>([]);
  filterChanged = output<FilterChange[]>();
  searchClicked = output<Record<string, unknown>>();
  filtersCleared = output<Record<string, unknown>>();
  private _activeRangeFilter = signal<string>('');
  activeRangeFilter = computed(() => this._activeRangeFilter());
  filterForm = new FormGroup<Record<string, FormControl<unknown>>>({});
  private destroy$ = new Subject<void>();
  private _selectedRangeField = signal<string>('');
  private _selectedRangeMin = signal<number | null>(null);
  private _selectedRangeMax = signal<number | null>(null);
  filtersContainer = viewChild<ElementRef<HTMLDivElement>>('filtersContainer');
  showLeftArrow = signal(false);
  showRightArrow = signal(false);
  private resizeObserver?: ResizeObserver;
  private filteredOptionsMap = new Map<string, Signal<FilterOption[]>>();
  private formSubscriptions: Subscription[] = [];
  constructor() {
    effect(onCleanup => {
      this.formSubscriptions.forEach(sub => sub.unsubscribe());
      this.formSubscriptions = [];

      this.filteredOptionsMap.clear();
      this.filterForm = new FormGroup({});

      this.filters().forEach(filter => {
        switch (filter.type) {
          case 'text':
          case 'select':
          case 'autocomplete':
            this.filterForm.addControl(filter.id, new FormControl<string>(''));
            break;
          case 'multi-select':
            this.filterForm.addControl(filter.id, new FormControl<string[]>([]));
            break;
          case 'date':
            this.filterForm.addControl(filter.id, new FormControl<Date | null>(null));
            break;
          case 'dateRange':
            this.filterForm.addControl(`${filter.id}_start`, new FormControl<Date | null>(null));
            this.filterForm.addControl(`${filter.id}_end`, new FormControl<Date | null>(null));
            break;
          case 'range':
            this.filterForm.addControl(filter.id, new FormControl<string>(''));
            this.filterForm.addControl(`${filter.id}_min`, new FormControl<number | null>(null));
            this.filterForm.addControl(`${filter.id}_max`, new FormControl<number | null>(null));
            break;
          default:
            this.filterForm.addControl(filter.id, new FormControl<unknown>(''));
        }

        if (filter.type === 'autocomplete') {
          const control = this.filterForm.get(filter.id) as FormControl;
          const valueSignal = signal('');
          const sub = control.valueChanges.pipe(startWith('')).subscribe(value => {
            valueSignal.set(value || '');
          });

          this.formSubscriptions.push(sub);
          const filteredOptions = computed(() => {
            const term = (valueSignal() || '').toLowerCase();
            const allOptions = filter.options || [];
            if (!term) return allOptions;
            return allOptions.filter(option => option.label.toLowerCase().includes(term));
          });

          this.filteredOptionsMap.set(filter.id, filteredOptions);
        }
      });

      onCleanup(() => {
        this.formSubscriptions.forEach(sub => sub.unsubscribe());
      });
    });

    effect(onCleanup => {
      const container = this.filtersContainer()?.nativeElement;
      if (container) {
        this.filters();
        Promise.resolve().then(() => this.updateArrowVisibility());
        this.resizeObserver = new ResizeObserver(this.updateArrowVisibility);
        this.resizeObserver.observe(container);
        container.addEventListener('scroll', this.updateArrowVisibility);
        onCleanup(() => {
          this.resizeObserver?.disconnect();
          container.removeEventListener('scroll', this.updateArrowVisibility);
        });
      }
    });
  }

  getFilteredOptions(filterId: string): Signal<FilterOption[]> {
    return this.filteredOptionsMap.get(filterId) ?? signal([]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateArrowVisibility = (): void => {
    const el = this.filtersContainer()?.nativeElement;
    if (!el) return;

    const hasScroll = el.scrollWidth > el.clientWidth;
    this.showLeftArrow.set(hasScroll && el.scrollLeft > 1);
    this.showRightArrow.set(hasScroll && el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
  };

  scroll(direction: 'left' | 'right'): void {
    const el = this.filtersContainer()?.nativeElement;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8 * (direction === 'left' ? -1 : 1);
    el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }

  getFilterControl(filterId: string): FormControl<unknown> {
    return this.filterForm.get(filterId) as FormControl<unknown>;
  }

  getFilterValue(filterId: string): unknown {
    const value = this.filterForm.get(filterId)?.value;
    if (filterId === 'rangeMin' || filterId === 'rangeMax') {
      return value === 0 ? 0 : value;
    }
    return value;
  }

  getRangeMinControl(filterId: string): FormControl<unknown> {
    const control = this.filterForm.get(`${filterId}_min`);
    if (!control) {
      throw new Error(`Control ${filterId}_min not found`);
    }
    return control as FormControl<unknown>;
  }

  getRangeMaxControl(filterId: string): FormControl<unknown> {
    const control = this.filterForm.get(`${filterId}_max`);
    if (!control) {
      throw new Error(`Control ${filterId}_max not found`);
    }
    return control as FormControl<unknown>;
  }

  setActiveRangeFilter(filterId: string): void {
    this._activeRangeFilter.set(filterId);
  }

  clearFilter(filterId: string): void {
    if (filterId === 'rangeMin' || filterId === 'rangeMax') {
      this.filterForm.get(filterId)?.setValue(null);
      if (filterId === 'rangeMin') {
        this._selectedRangeMin.set(null);
      } else {
        this._selectedRangeMax.set(null);
      }
      this.emitRangeValueChange(filterId);
    } else if (filterId.endsWith('_start') || filterId.endsWith('_end')) {
      this.filterForm.get(filterId)?.setValue(null);
      this.emitFilterChanges();
      return;
    } else if (this.filters().find(f => f.id === filterId && f.type === 'multi-select')) {
      this.filterForm.get(filterId)?.setValue([]);
    } else if (filterId === 'rangeField') {
      this.filterForm.get(filterId)?.setValue('');
      this._selectedRangeField.set('');
      this._selectedRangeMin.set(null);
      this._selectedRangeMax.set(null);
      this.filterForm.get('rangeMin')?.setValue(null);
      this.filterForm.get('rangeMax')?.setValue(null);
      this.emitFilterChanges();
    } else if (this.filters().find(f => f.id === filterId && f.type === 'range')) {
      this.filterForm.get(filterId)?.setValue('');
      const minControl = this.filterForm.get(`${filterId}_min`) as FormControl<number | null>;
      const maxControl = this.filterForm.get(`${filterId}_max`) as FormControl<number | null>;
      if (minControl) minControl.setValue(null);
      if (maxControl) maxControl.setValue(null);
      if (this.activeRangeFilter() === filterId) {
        this._activeRangeFilter.set('');
      }
      this.emitFilterChanges();
    } else {
      this.filterForm.get(filterId)?.setValue('');
    }
  }

  hasActiveFilters(): boolean {
    return Object.values(this.filterForm.value).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined && value !== '';
    });
  }

  isRangeEnabled(): boolean {
    return this._selectedRangeField() !== '';
  }

  getSelectedRangeMin(): number | undefined {
    return this._selectedRangeMin() ?? undefined;
  }

  getSelectedRangeMax(): number | undefined {
    return this._selectedRangeMax() ?? undefined;
  }

  hasMultiSelectValue(filterId: string): boolean {
    const value = this.getFilterValue(filterId);
    return Array.isArray(value) && value.length > 0;
  }

  clearDateRange(filterId: string): void {
    this.clearFilter(filterId + '_start');
    this.clearFilter(filterId + '_end');
    this.emitFilterChanges();
  }

  handleRangeFieldSelection(value: string): void {
    const previousField = this._selectedRangeField();
    if (previousField === value) {
      return;
    }

    this._selectedRangeField.set(value);
    this.filterForm.get('rangeField')?.setValue(value);
    const currentMin = this.getFilterValue('rangeMin');
    const currentMax = this.getFilterValue('rangeMax');
    const isFieldChanged = previousField !== value;
    const shouldResetValues = (isFieldChanged && previousField !== '') || (!currentMin && !currentMax);

    if (shouldResetValues) {
      this.filterForm.get('rangeMin')?.setValue(null);
      this.filterForm.get('rangeMax')?.setValue(null);
    }

    if (value) {
      const rangeFieldFilter = this.filters().find(f => f.id === 'rangeField');
      if (rangeFieldFilter && rangeFieldFilter.options) {
        const selectedOption = rangeFieldFilter.options.find(opt => opt.value === value);
        if (selectedOption) {
          const labelMatch = selectedOption.label.match(/\((\d+)-(\d+)\)/);
          if (labelMatch && labelMatch.length === 3) {
            const min = Number(labelMatch[1]);
            const max = Number(labelMatch[2]);
            this._selectedRangeMin.set(min);
            this._selectedRangeMax.set(max);
            if (shouldResetValues) {
              this.filterForm.get('rangeMin')?.setValue(min);
              this.filterForm.get('rangeMax')?.setValue(max);
            }
          }
        }
      }
    } else {
      this._selectedRangeMin.set(null);
      this._selectedRangeMax.set(null);
      this.filterForm.get('rangeMin')?.setValue(null);
      this.filterForm.get('rangeMax')?.setValue(null);
    }
  }

  submitFilters(): void {
    const rangeField = this.getFilterValue('rangeField') as string;
    const rangeMin = this.getFilterValue('rangeMin') as number | null;
    const rangeMax = this.getFilterValue('rangeMax') as number | null;
    const currentFilterValues = new Map<string, unknown>();
    this.filters().forEach(filter => {
      currentFilterValues.set(filter.id, this.getFilterValue(filter.id));
    });

    if (rangeField) {
      this._selectedRangeField.set(rangeField);
      if (rangeMin !== null && rangeMin !== undefined) {
        this._selectedRangeMin.set(Number(rangeMin));
      }
      if (rangeMax !== null && rangeMax !== undefined) {
        this._selectedRangeMax.set(Number(rangeMax));
      }
    }

    this.searchClicked.emit(this.filterForm.value);
    this.emitFilterChanges();
  }

  private emitFilterChanges(): void {
    const filterChanges: FilterChange[] = [];
    const rangeField = this.getFilterValue('rangeField') as string;
    const currentValues = new Map<string, unknown>();
    this.filters().forEach(filter => {
      const value = this.getFilterValue(filter.id);
      currentValues.set(filter.id, value);

      if (filter.id === 'rangeField') {
        filterChanges.push({ filterId: filter.id, value: value || '' });
        return;
      }

      if (filter.type === 'date' && value) {
        filterChanges.push({ filterId: filter.id, value: value });
      } else if (filter.id.endsWith('_start') || filter.id.endsWith('_end')) {
        filterChanges.push({ filterId: filter.id, value: value });
      } else if (filter.id === 'rangeMin' || filter.id === 'rangeMax') {
        filterChanges.push({ filterId: filter.id, value: value === 0 ? 0 : (value || null) });
      } else {
        filterChanges.push({ filterId: filter.id, value: value || '' });
      }
    });

    const rangeMin = currentValues.get('rangeMin');
    const rangeMax = currentValues.get('rangeMax');

    if ((rangeMin !== null && rangeMin !== undefined && rangeMin !== '') || (rangeMax !== null && rangeMax !== undefined && rangeMax !== '')) {
      if (!rangeField) {
        console.warn('Se especificaron valores de rango sin seleccionar un campo. Los valores de rango serán ignorados.');
      }
    }

    this.filterChanged.emit(filterChanges);
  }

  private emitRangeValueChange(filterId: string): void {
    const filterChanges: FilterChange[] = [];
    const rangeField = this.getFilterValue('rangeField') as string;
    filterChanges.push({ filterId: 'rangeField', value: rangeField });
    const currentMin = this.getFilterValue('rangeMin');
    const currentMax = this.getFilterValue('rangeMax');
    filterChanges.push({ filterId: 'rangeMin', value: filterId === 'rangeMin' ? null : (currentMin === 0 ? 0 : (currentMin || null)) });
    filterChanges.push({ filterId: 'rangeMax', value: filterId === 'rangeMax' ? null : (currentMax === 0 ? 0 : (currentMax || null)) });
    this.filters().forEach(filter => {
      if (filter.id !== 'rangeField' && filter.id !== 'rangeMin' && filter.id !== 'rangeMax') {
        const value = this.getFilterValue(filter.id);
        filterChanges.push({ filterId: filter.id, value: value || '' });
      }
    });
    this.filterChanged.emit(filterChanges);
  }

  clearSingleRangeFilter(filterId: string): void {
    this.filterForm.get(filterId)?.setValue(null);
    if (filterId === 'rangeMin') {
      this._selectedRangeMin.set(null);
    } else if (filterId === 'rangeMax') {
      this._selectedRangeMax.set(null);
    }

    const filterChanges: FilterChange[] = [];
    const rangeField = this.getFilterValue('rangeField') as string;
    filterChanges.push({ filterId: 'rangeField', value: rangeField });
    const currentMin = this.getFilterValue('rangeMin');
    const currentMax = this.getFilterValue('rangeMax');
    filterChanges.push({ filterId: 'rangeMin', value: currentMin === 0 ? 0 : (currentMin || null) });
    filterChanges.push({ filterId: 'rangeMax', value: currentMax === 0 ? 0 : (currentMax || null) });
    this.filters().forEach(filter => {
      if (filter.id !== 'rangeField' && filter.id !== 'rangeMin' && filter.id !== 'rangeMax') {
        const value = this.getFilterValue(filter.id);
        filterChanges.push({ filterId: filter.id, value: value || '' });
      }
    });
  }

  clearAllFilters(): void {
    const currentValues = this.filterForm.value;
    this.filtersCleared.emit(currentValues);
    this.filterForm.reset();
    this.emitFilterChanges();
  }
}
